import {
  Alert,
  Box,
  Button,
  FormField,
  Modal,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import { checkIfAutoTransferable } from '@parallel-fluidics/shared/utils';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useGetOrdersByProjectId } from 'hooks/use-basicQueries';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

export default function TransferTravelerModal({
  isOpen,
  onClose,
  traveler,
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const { data: orders = [] } = useGetOrdersByProjectId(traveler?.project);
  const availableLineItemsMap = useMemo(() => {
    const lineItemIdMap = new Map();
    orders.forEach((order) => {
      order.shipments.forEach((shipment) => {
        shipment.lineItems.forEach((lineItem) => {
          const isTransferable = checkIfAutoTransferable({
            from: {
              projectId: traveler.project,
              lineItemId: traveler.lineItem,
              steps: traveler.steps,
            },
            target: {
              projectId: lineItem.projectId,
              lineItemId: lineItem.id,
              steps: lineItem.steps,
              type: lineItem.type,
            },
          });

          if (isTransferable) {
            lineItemIdMap.set(lineItem.id, {
              value: lineItem.id,
              label: `${lineItem.projectId}-${lineItem.pPartId}`,
              description: (
                <div>
                  <div>
                    <strong>Steps:</strong>
                    {' '}
                    {lineItem.steps?.map((step) => `${step.pPartId || step.name} (${step.type})`).join(' -> ')}
                  </div>
                  <div>
                    <strong>Order Name:</strong>
                    {` ${order.name} (${order.id})`}
                  </div>
                  <SpaceBetween direction="horizontal" size="s">
                    <div>
                      <strong>Order Date:</strong>
                      {' '}
                      {order.orderDate}
                    </div>
                    <div>
                      <strong>Target Ship Date:</strong>
                      {' '}
                      {shipment.targetShipDate}
                    </div>
                    <div>
                      <strong>Order Status:</strong>
                      {' '}
                      {order.status}
                    </div>
                  </SpaceBetween>
                </div>
              ),
            });
          }
        });
      });
    });
    return lineItemIdMap;
  }, [orders, traveler?.lineItem, traveler?.project, traveler?.steps]);

  const handleTransfer = async () => {
    try {
      setErrorMessage(null);
      if (!selectedOption) {
        throw new Error('No line item selected for transfer');
      }
      setIsTransferring(true);
      const transferTraveler = httpsCallable(getFunctions(), 'transferTravelerV2');
      await transferTraveler({
        travelerId: traveler.id,
        targetLineItemId: selectedOption.value,
      });
      toast.success('Traveler transferred successfully');
      onClose();
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred while transferring the traveler.');
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <Modal
      visible={isOpen}
      onDismiss={onClose}
      header="Transfer Traveler"
      footer={(
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={isTransferring} onClick={handleTransfer} disabled={availableLineItemsMap.size === 0}>Transfer</Button>
          </SpaceBetween>
        </Box>
      )}
    >
      <SpaceBetween size="m">
        {errorMessage && (
          <Alert
            type="error"
            header={errorMessage}
          />
        )}
        {availableLineItemsMap.size > 0 ? (
          <FormField
            label="Select a new line item for the traveler:"
            description={`Current traveler belongs to order ID: ${traveler.order}`}
          >
            <Select
              selectedOption={selectedOption}
              onChange={({ detail }) => setSelectedOption(detail.selectedOption)}
              options={Array.from(availableLineItemsMap.values())}
              placeholder="Select a line item"
            />
          </FormField>
        ) : (
          <p>No available line items to transfer this traveler.</p>
        )}
      </SpaceBetween>
    </Modal>
  );
}

TransferTravelerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  traveler: PropTypes.shape({
    id: PropTypes.string,
    project: PropTypes.string,
    lineItem: PropTypes.string,
    order: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
      pPartId: PropTypes.string.isRequired,
    })),
  }).isRequired,
};
